/**
 * Roles & Permissions Admin (Story 5.1 AC-6 follow-up).
 *
 * Lets a platform admin see every role and toggle exactly which
 * permission codenames it grants, backed by the existing
 * GET/PUT /api/v1/admin/roles(/{id}/permissions) endpoints -- those
 * already existed and worked; there was simply no UI for them, and
 * every non-admin role in this environment (including the TASSCC
 * conference_attendee accounts) had an empty permission set as a result.
 *
 * Grain note: the profiler codenames are shared today (profiler:read/execute
 * gate the Profiler, Data Guardian/Masking and the project outputs routes;
 * profiler:configure is the tenant-wide profiler config) -- there is no
 * finer-grained string to separate them. The UI surfaces this explicitly
 * next to that group rather than implying a precision the backend doesn't
 * have. Semantic Intelligence, Metadata Curator, Backlog Architect, Pipeline
 * Engineer and Data Validation each have their own category and codenames
 * (semantic:*, metadata:*, backlog:*, pipeline_engineer:*, validation:*),
 * enforced by their own routers (#837), so they are granted independently.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import { rbacAdmin, type PermissionOut, type RoleOut } from "../../../apiv1";

interface Props {
  onBack: () => void;
}

// Friendly group labels + ordering. Falls back to the raw category string
// for anything not listed here (e.g. a future agent's permissions).
export const CATEGORY_LABELS: Record<string, string> = {
  admin: "Platform Administration",
  agent: "Agent Catalog",
  profiler: "Profiler — also gates Data Guardian (Masking) and project outputs",
  semantic: "Semantic Intelligence",
  metadata: "Metadata Curator",
  backlog: "Backlog Architect",
  pipeline_engineer: "Pipeline Engineer",
  validation: "Data Validation",
  mdm: "Master Data Management",
  converter: "Data Converter",
  rule: "Data Quality Rules",
  reporting: "Reporting & Export",
  pipeline: "Pipelines",
  connection: "Data Connections",
  upload: "File Uploads",
  hitl: "Human-in-the-Loop Review",
  lineage: "Data Lineage",
  schedule: "Scheduling",
  deployment: "Deployment",
  error: "Error Handling",
  metering: "Metering / Spend",
  ops: "Observability",
  audit: "Audit",
  config: "Configuration",
  notifications: "Notifications",
};

export const CATEGORY_ORDER = [
  "agent",
  "profiler",
  "semantic",
  "metadata",
  "backlog",
  "pipeline_engineer",
  "validation",
  "mdm",
  "converter",
  "rule",
  "reporting",
  "pipeline",
  "connection",
  "upload",
  "hitl",
  "lineage",
  "schedule",
  "deployment",
  "metering",
  "ops",
  "audit",
  "config",
  "error",
  "notifications",
  "admin",
];

// Categories where every codename is coupled to the same set of agents --
// shown with a callout instead of being presented as independently
// selectable per-agent switches.
export const SHARED_GRAIN_CATEGORIES = new Set(["profiler"]);
export const SHARED_GRAIN_NOTE =
  "profiler:read and profiler:execute are shared by the Profiler, Data Guardian (Masking) and project outputs — there's no way to grant one without the others today. Semantic, Metadata, Backlog, Pipeline Engineer and Validation are separate groups below.";

export function RolePermissionsAdmin({ onBack }: Props) {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [permissions, setPermissions] = useState<PermissionOut[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // New-role form
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [creatingRole, setCreatingRole] = useState(false);
  const roleNamePattern = /^[a-z][a-z0-9_]*$/;

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [roleList, permList] = await Promise.all([
        rbacAdmin.listRoles(),
        rbacAdmin.listPermissions(),
      ]);
      setRoles(roleList);
      setPermissions(permList);
      if (roleList.length > 0) {
        const first = roleList[0];
        setSelectedRoleId(first.id);
        setDraft(new Set(first.permissions));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const selectedRole = useMemo(
    () => roles.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );

  // platform_admin bypasses the permission registry entirely at
  // enforcement time (hardcoded wildcard in RBACService) — it always has
  // every permission regardless of what's checked here. Editing would be
  // purely cosmetic (and the API rejects it), so the checklist is shown
  // read-only, fully checked, for this role.
  const isPlatformAdmin = selectedRole?.name === "platform_admin";

  const grouped = useMemo(() => {
    const groups: Record<string, PermissionOut[]> = {};
    for (const p of permissions) {
      (groups[p.category] ??= []).push(p);
    }
    for (const list of Object.values(groups)) {
      list.sort((a, b) => a.codename.localeCompare(b.codename));
    }
    return groups;
  }, [permissions]);

  const orderedCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter((c) => grouped[c]);
    const rest = Object.keys(grouped)
      .filter((c) => !CATEGORY_ORDER.includes(c))
      .sort();
    return [...known, ...rest];
  }, [grouped]);

  const isDirty = useMemo(() => {
    if (!selectedRole) return false;
    const original = new Set(selectedRole.permissions);
    if (original.size !== draft.size) return true;
    for (const p of draft) if (!original.has(p)) return true;
    return false;
  }, [selectedRole, draft]);

  function selectRole(role: RoleOut) {
    if (isDirty && !confirm("Discard unsaved permission changes?")) return;
    setSelectedRoleId(role.id);
    setDraft(new Set(role.permissions));
    setSaved(false);
    setError(null);
  }

  function toggle(codename: string) {
    const next = new Set(draft);
    if (next.has(codename)) next.delete(codename);
    else next.add(codename);
    setDraft(next);
    setSaved(false);
  }

  function toggleCategory(category: string, allChecked: boolean) {
    const next = new Set(draft);
    for (const p of grouped[category] ?? []) {
      if (allChecked) next.delete(p.codename);
      else next.add(p.codename);
    }
    setDraft(next);
    setSaved(false);
  }

  function selectAll() {
    setDraft(new Set(permissions.map((p) => p.codename)));
    setSaved(false);
  }

  function deselectAll() {
    setDraft(new Set());
    setSaved(false);
  }

  async function handleCreateRole() {
    if (!roleNamePattern.test(newRoleName)) return;
    setCreatingRole(true);
    setError(null);
    try {
      const created = await rbacAdmin.createRole({
        name: newRoleName,
        description: newRoleDescription.trim() || null,
      });
      setRoles((prev) => [...prev, created]);
      setSelectedRoleId(created.id);
      setDraft(new Set(created.permissions));
      setSaved(false);
      setNewRoleName("");
      setNewRoleDescription("");
      setShowCreateRole(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingRole(false);
    }
  }

  async function handleSave() {
    if (!selectedRole) return;
    if (draft.size === 0) {
      setError(
        "A role needs at least one permission (the API rejects an empty set). Uncheck everything but agent:read if you want a near-read-only role.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await rbacAdmin.updateRolePermissions(
        selectedRole.id,
        Array.from(draft),
      );
      setRoles((prev) =>
        prev.map((r) =>
          r.id === selectedRole.id
            ? { ...r, permissions: result.permissions }
            : r,
        ),
      );
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Roles & Permissions</h1>
          <p className="text-sm text-white/50 mt-1">
            Choose which agents and actions each role can use. Changes apply
            within ~60s (cache invalidation), no redeploy needed.
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-3 py-2 bg-white/10 hover:bg-white/15 text-sm rounded"
        >
          Back
        </button>
      </div>

      {/* Module tabs — Users / Roles & Permissions are one admin area */}
      <div className="flex items-center gap-2 border-b border-white/8 mb-6">
        <button
          onClick={() => navigate("/admin/users")}
          className="px-4 py-2 text-sm font-semibold text-white/40 hover:text-white/70 flex items-center gap-1.5"
        >
          <Users size={14} /> Users
        </button>
        <button
          className="px-4 py-2 text-sm font-semibold text-brand-400 border-b-2 border-brand-500"
          disabled
        >
          Roles & Permissions
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-[240px_1fr] gap-6">
          {/* Role list */}
          <div className="space-y-1">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => selectRole(role)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition ${
                  role.id === selectedRoleId
                    ? "bg-brand-500/20 text-brand-300 border border-brand-500/40"
                    : "hover:bg-white/5 border border-transparent"
                }`}
              >
                <div className="font-medium">{role.name}</div>
                <div className="text-[11px] text-white/40 mt-0.5">
                  {role.permissions.length} permission
                  {role.permissions.length === 1 ? "" : "s"}
                  {role.is_system_role ? " · system role" : ""}
                </div>
              </button>
            ))}

            {showCreateRole ? (
              <div className="mt-2 p-3 border border-white/10 rounded-lg space-y-2">
                <input
                  type="text"
                  autoFocus
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value.toLowerCase())}
                  placeholder="role_name"
                  className="w-full px-2 py-1.5 bg-surface-300/50 border border-white/8 rounded text-xs font-mono text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
                {newRoleName && !roleNamePattern.test(newRoleName) && (
                  <div className="text-[11px] text-red-400">
                    lowercase letters, digits, underscores — must start with a
                    letter
                  </div>
                )}
                <input
                  type="text"
                  value={newRoleDescription}
                  onChange={(e) => setNewRoleDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-2 py-1.5 bg-surface-300/50 border border-white/8 rounded text-xs text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCreateRole}
                    disabled={
                      creatingRole || !roleNamePattern.test(newRoleName)
                    }
                    className="px-3 py-1 bg-brand-500 hover:bg-brand-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs rounded"
                  >
                    {creatingRole ? "Creating…" : "Create"}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateRole(false);
                      setNewRoleName("");
                      setNewRoleDescription("");
                    }}
                    className="px-3 py-1 text-white/50 hover:text-white/80 text-xs rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowCreateRole(true)}
                className="w-full text-left px-3 py-2 rounded text-sm text-white/40 hover:text-white/70 hover:bg-white/5 border border-dashed border-white/15 flex items-center gap-1.5 mt-2"
              >
                <Plus size={14} /> New role
              </button>
            )}
          </div>

          {/* Permission editor */}
          <div>
            {!selectedRole ? (
              <div className="text-white/40 text-sm">
                Select a role to view its permissions.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-semibold">{selectedRole.name}</div>
                    {selectedRole.description && (
                      <div className="text-[11px] text-white/40 mt-0.5">
                        {selectedRole.description}
                      </div>
                    )}
                  </div>
                  {!isPlatformAdmin && (
                    <div className="flex items-center gap-2">
                      {saved && !isDirty && (
                        <span className="text-[11px] text-emerald-400">
                          Saved
                        </span>
                      )}
                      <button
                        onClick={selectAll}
                        disabled={draft.size === permissions.length}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded"
                      >
                        Select all
                      </button>
                      <button
                        onClick={deselectAll}
                        disabled={draft.size === 0}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded"
                      >
                        Deselect all
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        className="px-3 py-1.5 bg-brand-500 hover:bg-brand-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded"
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  )}
                </div>

                {isPlatformAdmin && (
                  <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs rounded">
                    platform_admin always has every permission — it's a
                    hardcoded bypass, not a stored grant, so this list is shown
                    read-only and can't be edited here.
                  </div>
                )}

                <div className="space-y-4">
                  {orderedCategories.map((category) => {
                    const perms = grouped[category] ?? [];
                    const allChecked = perms.every((p) =>
                      draft.has(p.codename),
                    );
                    const someChecked = perms.some((p) =>
                      draft.has(p.codename),
                    );
                    return (
                      <div
                        key={category}
                        className="border border-white/10 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <label
                            className={`flex items-center gap-2 text-xs font-medium text-white/70 ${isPlatformAdmin ? "" : "cursor-pointer"}`}
                          >
                            <input
                              type="checkbox"
                              checked={allChecked}
                              disabled={isPlatformAdmin}
                              ref={(el) => {
                                if (el)
                                  el.indeterminate = someChecked && !allChecked;
                              }}
                              onChange={() =>
                                toggleCategory(category, allChecked)
                              }
                            />
                            {CATEGORY_LABELS[category] ?? category}
                          </label>
                        </div>
                        {SHARED_GRAIN_CATEGORIES.has(category) && (
                          <div className="text-[11px] text-amber-400/80 mb-2 pl-6">
                            {SHARED_GRAIN_NOTE}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 pl-6">
                          {perms.map((p) => (
                            <label
                              key={p.codename}
                              className={`flex items-center gap-2 text-xs py-0.5 px-1 rounded ${isPlatformAdmin ? "" : "cursor-pointer hover:bg-white/5"}`}
                              title={p.description ?? undefined}
                            >
                              <input
                                type="checkbox"
                                checked={draft.has(p.codename)}
                                disabled={isPlatformAdmin}
                                onChange={() => toggle(p.codename)}
                              />
                              <span className="font-mono text-white/70">
                                {p.codename}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
